import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Search, 
  X,
  Loader,
  Truck,
  Edit2,
  Trash2,
  Plus,
  Filter
} from 'lucide-react';
import TransporteModal from '../components/Customers/TransporteModal';
import DeleteConfirmModal from '../components/Customers/DeleteConfirmModal';
import { Can } from '../components/Auth/Can';
import {
  selectFilteredTransportes,
  selectTransportesFilters,
  selectTransportesLoading,
  selectTransportesError,
  setFilters,
  clearFilters,
  fetchTransportes,
  createTransporte,
  updateTransporte,
  deleteTransporte
} from '../store/slices/transportesSlice';

const Transportes = () => {
  const dispatch = useDispatch();
  const transportes = useSelector(selectFilteredTransportes);
  const filters = useSelector(selectTransportesFilters);
  const loading = useSelector(selectTransportesLoading);
  const error = useSelector(selectTransportesError);
  
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedTransporte, setSelectedTransporte] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [transporteToDelete, setTransporteToDelete] = useState(null);

  const tipoOptions = [
    {
      value: 'all',
      label: 'Todos',
      description: 'Ver todos los transportes'
    },
    {
      value: 'exportacion',
      label: 'Exportación',
      description: 'USA y Canadá'
    },
    {
      value: 'nacional',
      label: 'Nacional',
      description: 'Chile'
    }
  ];

  // Cargar transportes al montar el componente y cuando cambian los filtros
  useEffect(() => {
    dispatch(fetchTransportes(filters));
  }, [dispatch, filters]);

  const handleFilterChange = (key, value) => {
    dispatch(setFilters({ [key]: value }));
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  const handleNewTransporte = () => {
    setSelectedTransporte(null);
    setModalMode('create');
    setShowModal(true);
  };

  const handleEditTransporte = (transporte) => {
    setSelectedTransporte(transporte);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDeleteTransporte = (transporte) => {
    setTransporteToDelete(transporte);
    setShowDeleteModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTransporte(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setTransporteToDelete(null);
  };

  const handleSaveTransporte = async (transporteDataOrId, transporteData) => {
    if (modalMode === 'edit') {
      await dispatch(updateTransporte({ 
        id: transporteDataOrId, 
        transporteData 
      }));
    } else {
      await dispatch(createTransporte(transporteDataOrId));
    }
  };

  const handleConfirmDelete = async () => {
    if (transporteToDelete) {
      await dispatch(deleteTransporte(transporteToDelete.id));
      handleCloseDeleteModal();
    }
  };

  const getTipoBadgeClass = (tipo) => {
    return tipo === 'exportacion' ? 'badge-exportacion' : 'badge-nacional';
  };

  return (
    <div className="transportes-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Transportes
          </h1>
          <p className="text-lg text-gray-600 mt-2">
            Gestión de empresas de transporte
          </p>
        </div>
        <Can I="create" this="customers:transport">
          <button 
            className="btn btn-primary"
            onClick={handleNewTransporte}
          >
            <Plus size={20} />
            Nuevo Transporte
          </button>
        </Can>
      </div>

      {/* Tipo Selector */}
      <div className="card tipo-selector-card">
        <div className="tipo-selector-header">
          <Filter size={18} />
          <span>Tipo de Transporte</span>
        </div>
        <div className="tipo-selector-buttons">
          {tipoOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`tipo-button ${filters.tipo === option.value ? 'active' : ''}`}
              onClick={() => handleFilterChange('tipo', option.value)}
            >
              <div className="tipo-button-icon">
                <Truck size={18} />
              </div>
              <div className="tipo-button-text">
                <span className="tipo-label">{option.label}</span>
                <span className="tipo-description">{option.description}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="card">
        <div className="filters-section">
          <div className="search-bar">
            <Search size={20} />
            <input
              type="text"
              placeholder="Buscar transportes..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="search-input"
            />
          </div>
          
          {(filters.search || filters.tipo !== 'all') && (
            <div className="filter-actions">
              <button 
                className="btn btn-outline"
                onClick={handleClearFilters}
              >
                <X size={20} />
                Limpiar
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <p className="text-gray-600">
          Mostrando {transportes.length} transporte{transportes.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <div className="alert alert-error">
            <span>Error: {error}</span>
            <button 
              className="btn btn-sm btn-outline"
              onClick={() => dispatch(fetchTransportes(filters))}
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {/* Transportes Table */}
      <div className="card">
        {loading ? (
          <div className="loading-state">
            <Loader className="animate-spin" size={32} />
            <p className="text-gray-600 mt-4">Cargando transportes...</p>
          </div>
        ) : transportes.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {transportes.map((transporte) => (
                  <tr key={transporte.id}>
                    <td>
                      <div className="transporte-info">
                        <div className="transporte-icon">
                          <Truck size={20} />
                        </div>
                        <div>
                          <div className="font-medium">{transporte.nombre}</div>
                          <div className="text-sm text-gray-500">ID: {transporte.id}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${getTipoBadgeClass(transporte.tipo)}`}>
                        {transporte.tipo === 'exportacion' ? 'Exportación' : 'Nacional'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Can I="update" this="customers:transport">
                          <button
                            className="btn-icon btn-icon-edit"
                            onClick={() => handleEditTransporte(transporte)}
                            title="Editar transporte"
                          >
                            <Edit2 size={18} />
                          </button>
                        </Can>
                        <Can I="delete" this="customers:transport">
                          <button
                            className="btn-icon btn-icon-delete"
                            onClick={() => handleDeleteTransporte(transporte)}
                            title="Eliminar transporte"
                          >
                            <Trash2 size={18} />
                          </button>
                        </Can>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">🚚</div>
            <h3 className="text-lg font-medium text-gray-900 mt-4">
              No se encontraron transportes
            </h3>
            <p className="text-gray-500 mt-2">
              {filters.search || filters.tipo !== 'all' 
                ? 'Ajusta los filtros para ver más resultados'
                : 'Comienza agregando un nuevo transporte'}
            </p>
            {!filters.search && filters.tipo === 'all' && (
              <button 
                className="btn btn-primary mt-4"
                onClick={handleNewTransporte}
              >
                <Plus size={20} />
                Nuevo Transporte
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showModal && (
        <TransporteModal
          transporte={selectedTransporte}
          onClose={handleCloseModal}
          onSave={handleSaveTransporte}
          mode={modalMode}
        />
      )}

      {showDeleteModal && (
        <DeleteConfirmModal
          item={transporteToDelete}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          loading={loading}
        />
      )}

      <style jsx>{`
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: var(--spacing-6);
        }

        .tipo-selector-card {
          margin-bottom: var(--spacing-6);
        }

        .tipo-selector-header {
          display: flex;
          align-items: center;
          gap: var(--spacing-2);
          margin-bottom: var(--spacing-4);
          font-weight: 600;
          color: var(--gray-700);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .tipo-selector-buttons {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: var(--spacing-3);
        }

        .tipo-button {
          display: flex;
          align-items: center;
          gap: var(--spacing-3);
          padding: var(--spacing-4);
          border-radius: var(--radius-lg);
          border: 1px solid var(--gray-200);
          background: white;
          text-align: left;
          transition: all 0.2s ease-in-out;
          cursor: pointer;
        }

        .tipo-button:hover {
          border-color: #285F49;
          box-shadow: 0 10px 30px -12px rgba(40, 95, 73, 0.4);
        }

        .tipo-button.active {
          border-color: #285F49;
          background: linear-gradient(135deg, rgba(40, 95, 73, 0.08), rgba(40, 95, 73, 0.02));
        }

        .tipo-button-icon {
          width: 48px;
          height: 48px;
          border-radius: var(--radius-lg);
          background-color: rgba(40, 95, 73, 0.12);
          color: #285F49;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .tipo-button.active .tipo-button-icon {
          background-color: #285F49;
          color: white;
        }

        .tipo-button-text {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-1);
        }

        .tipo-label {
          font-weight: 600;
          color: var(--gray-900);
          font-size: var(--font-size-lg);
        }

        .tipo-description {
          font-size: var(--font-size-sm);
          color: var(--gray-600);
        }
        
        .filters-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: var(--spacing-4);
          margin-bottom: var(--spacing-4);
        }
        
        .search-bar {
          position: relative;
          flex: 1;
          max-width: 400px;
        }
        
        .search-bar svg {
          position: absolute;
          left: var(--spacing-3);
          top: 50%;
          transform: translateY(-50%);
          color: var(--gray-400);
        }
        
        .search-input {
          width: 100%;
          padding: var(--spacing-3) var(--spacing-3) var(--spacing-3) var(--spacing-10);
          border: 2px solid var(--gray-300);
          border-radius: var(--radius-md);
          font-size: var(--font-size-base);
          transition: border-color 0.2s ease-in-out;
        }
        
        .search-input:focus {
          outline: none;
          border-color: #285F49;
          box-shadow: 0 0 0 3px rgba(40, 95, 73, 0.1);
        }
        
        .filter-actions {
          display: flex;
          gap: var(--spacing-2);
        }
        
        .results-summary {
          margin-bottom: var(--spacing-4);
        }
        
        .table-container {
          overflow-x: auto;
        }
        
        .table th:nth-child(1) {
          width: 50%;
        }
        
        .table th:nth-child(2) {
          width: 30%;
        }
        
        .table th:nth-child(3) {
          width: 20%;
        }
        
        .transporte-info {
          display: flex;
          align-items: center;
          gap: var(--spacing-3);
        }
        
        .transporte-icon {
          width: 40px;
          height: 40px;
          min-width: 40px;
          min-height: 40px;
          background-color: rgba(40, 95, 73, 0.1);
          color: #285F49;
          border-radius: var(--radius-md);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .badge {
          display: inline-block;
          padding: var(--spacing-1) var(--spacing-3);
          border-radius: var(--radius-full);
          font-size: var(--font-size-sm);
          font-weight: 600;
        }

        .badge-exportacion {
          background-color: #d1fae5;
          color: #065f46;
        }

        .badge-nacional {
          background-color: #d1fae5;
          color: #065f46;
        }
        
        .action-buttons {
          display: flex;
          gap: var(--spacing-2);
          align-items: center;
        }
        
        .btn-icon {
          background: none;
          border: none;
          padding: var(--spacing-2);
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .btn-icon-edit {
          color: #285F49;
        }
        
        .btn-icon-edit:hover {
          background-color: rgba(40, 95, 73, 0.1);
        }

        .btn-icon-delete {
          color: #dc2626;
        }
        
        .btn-icon-delete:hover {
          background-color: rgba(220, 38, 38, 0.1);
        }
        
        .empty-state {
          text-align: center;
          padding: var(--spacing-12) var(--spacing-6);
        }
        
        .empty-icon {
          font-size: 4rem;
          margin-bottom: var(--spacing-4);
        }
        
        .loading-state {
          text-align: center;
          padding: var(--spacing-12) var(--spacing-6);
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .error-message {
          margin-bottom: var(--spacing-4);
        }
        
        .alert {
          padding: var(--spacing-4);
          border-radius: var(--radius-md);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .alert-error {
          background-color: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: var(--spacing-2);
          padding: var(--spacing-3) var(--spacing-5);
          border-radius: var(--radius-md);
          font-weight: 600;
          font-size: var(--font-size-sm);
          cursor: pointer;
          transition: all 0.2s ease-in-out;
          border: none;
        }

        .btn-outline {
          background: white;
          border: 2px solid var(--gray-300);
          color: var(--gray-700);
        }

        .btn-outline:hover {
          background-color: var(--gray-50);
          border-color: var(--gray-400);
        }

        .btn-primary {
          background: linear-gradient(135deg, #EA4E44, #d44439);
          color: white;
          box-shadow: 0 4px 6px -1px rgba(234, 78, 68, 0.3);
        }

        .btn-primary:hover {
          box-shadow: 0 10px 15px -3px rgba(234, 78, 68, 0.4);
          transform: translateY(-1px);
        }
        
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            gap: var(--spacing-4);
          }

          .page-header .btn {
            width: 100%;
            justify-content: center;
          }

          .tipo-selector-buttons {
            grid-template-columns: 1fr;
          }
          
          .filters-section {
            flex-direction: column;
            align-items: stretch;
          }
          
          .search-bar {
            max-width: none;
          }
          
          .table {
            font-size: var(--font-size-sm);
          }
          
          .transporte-info {
            flex-direction: column;
            align-items: flex-start;
            gap: var(--spacing-2);
          }
          
          .transporte-icon {
            width: 32px;
            height: 32px;
            min-width: 32px;
            min-height: 32px;
          }
        }
      `}</style>
    </div>
  );
};

export default Transportes;
